/**
 * WindowOrderPicker — the ORDER BY sub-editor of `WindowSpecEditor`: one row
 * per order item (expression + ASC/DESC `TogglePill` + optional NULLS
 * FIRST/LAST dropdown) plus a schema-driven `[+ add]` column chooser. Mirrors
 * the `OrderByItemRow` / `OrderByNullsDropdown` VISUAL idiom, but commits through
 * the window `OverBodySpec` (via the parent's `modifySelectWindow`) rather than
 * the statement-level `modifyOrderBy` chain those components use.
 *
 * NULLS placement is dialect-gated (`showNulls`) exactly like the top-level
 * ORDER BY section — MSSQL / MySQL don't render the NULLS control.
 */
import { ArrowUpDown, ChevronDown } from 'lucide-react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../../../components/ui/dropdown-menu';
import { cn } from '../../../../../lib/utils';
import { vqeCopy } from '../../ux/vqeCopy';
import { CommandPickerPopover, type CommandPickerItem } from '../common/CommandPickerPopover';
import { RemoveButton } from '../common/RemoveButton';
import { TogglePill } from '../common/TogglePill';
import type { ParsedOrderItem } from './parseSelectItem';

export interface WindowOrderPickerProps {
  readonly orderBy: readonly ParsedOrderItem[];
  readonly onChange: (next: readonly ParsedOrderItem[]) => void;
  readonly testIdPrefix: string;
  readonly showNulls: boolean;
  readonly columnItems: readonly CommandPickerItem[];
  readonly resolveExpr: (key: string) => string | undefined;
  readonly loading: boolean;
}

const NULLS_TRIGGER_CLASS =
  'inline-flex items-center gap-1 rounded bg-background px-1.5 py-0.5 text-[10px] font-semibold ' +
  'uppercase tracking-wide text-muted-foreground cursor-pointer hover:bg-accent hover:text-foreground ' +
  'hover:ring-1 hover:ring-ring/50';

export function WindowOrderPicker({
  orderBy,
  onChange,
  testIdPrefix,
  showNulls,
  columnItems,
  resolveExpr,
  loading,
}: WindowOrderPickerProps) {
  const prefix = `${testIdPrefix}-order`;

  const patch = (index: number, next: Partial<ParsedOrderItem>): void => {
    onChange(orderBy.map((item, i) => (i === index ? { ...item, ...next } : item)));
  };
  const removeAt = (index: number): void => onChange(orderBy.filter((_, i) => i !== index));
  const addColumn = (key: string): void => {
    const expr = resolveExpr(key);
    if (!expr) return;
    onChange([...orderBy, { expression: expr, direction: null, nulls: null }]);
  };

  return (
    <div className="flex flex-col gap-1.5" data-testid={`${prefix}-rows`}>
      <div className="flex items-center gap-2">
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Order by</span>
        {orderBy.length === 0 ? (
          <span className="text-[11px] italic text-muted-foreground" data-testid={`${prefix}-empty`}>
            {vqeCopy.window.noOrder}
          </span>
        ) : null}
      </div>
      {orderBy.map((item, index) => (
        <div
          key={`${item.expression}-${index}`}
          className="flex items-center gap-2 rounded-md border border-border bg-muted/30 px-2 py-1"
          data-testid={`${prefix}-item-${index}`}
        >
          <span className="flex-1 truncate font-mono text-xs" data-testid={`${prefix}-item-expr-${index}`}>
            {item.expression}
          </span>
          <TogglePill
            testId={`${prefix}-item-direction-${index}`}
            ariaLabel={`Toggle sort direction (currently ${item.direction ?? 'ASC'})`}
            pressed={item.direction === 'DESC'}
            icon={<ArrowUpDown className="h-3 w-3" aria-hidden />}
            tooltip="Click to cycle ASC → DESC"
            onClick={() => patch(index, { direction: item.direction === 'DESC' ? 'ASC' : 'DESC' })}
          >
            {item.direction ?? 'ASC'}
          </TogglePill>
          {showNulls ? (
            <NullsDropdown
              testId={`${prefix}-item-nulls-${index}`}
              nulls={item.nulls}
              onSelect={(nulls) => patch(index, { nulls })}
            />
          ) : null}
          <RemoveButton
            testId={`${prefix}-remove-${index}`}
            ariaLabel={`Remove sort ${item.expression}`}
            disabled={false}
            onClick={() => removeAt(index)}
          />
        </div>
      ))}
      <CommandPickerPopover
        testId={`${prefix}-add`}
        triggerLabel={vqeCopy.actions.addSort}
        disabled={false}
        tooltip={vqeCopy.window.addOrder}
        items={columnItems}
        onSelect={addColumn}
        loading={loading}
        emptyMessage={vqeCopy.window.noColumnsInScope}
      />
    </div>
  );
}

interface NullsDropdownProps {
  readonly testId: string;
  readonly nulls: 'FIRST' | 'LAST' | null;
  readonly onSelect: (next: 'FIRST' | 'LAST' | null) => void;
}

const NULLS_OPTIONS: readonly { readonly value: 'FIRST' | 'LAST' | null; readonly key: string; readonly label: string }[] = [
  { value: null, key: 'default', label: 'default' },
  { value: 'FIRST', key: 'first', label: 'FIRST' },
  { value: 'LAST', key: 'last', label: 'LAST' },
];

function NullsDropdown({ testId, nulls, onSelect }: NullsDropdownProps) {
  const label = nulls ?? 'default';
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        type="button"
        aria-label={`NULLS placement (currently ${label})`}
        data-testid={testId}
        className={NULLS_TRIGGER_CLASS}
      >
        NULLS: {label}
        <ChevronDown className="h-3 w-3" aria-hidden />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" data-testid={`${testId}-menu`}>
        {NULLS_OPTIONS.map((opt) => (
          <DropdownMenuItem
            key={opt.key}
            data-testid={`${testId}-option-${opt.key}`}
            onSelect={() => onSelect(opt.value)}
            className={cn('text-xs', opt.value === nulls ? 'font-semibold' : undefined)}
          >
            {opt.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
